import { ForbiddenError } from '@ember-data/adapter/error';
import {
  MESSAGES_PER_PAGE,
  emptyInfinityModel,
  pageNumberInfinityModel,
  pageNumberInfinityOptions,
} from '../../utils/messaging/infinity-model';
import { MODEL_NAMES } from '../../constants/index';
import { inactiveReasonsFromConflicts } from '../../utils/messaging/conversation';
import { service } from '@ember/service';
import { task } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import MessagingRoutesBaseHandler from './base-handler.js';

export default class MessagingRoutesConversationHandler extends MessagingRoutesBaseHandler {
  @service('spds/toast') toast;
  @service infinity;
  @service intl;
  @service messaging;
  @service messagingLogging;
  @service messagingNavigation;
  @service store;

  @tracked conversation;
  @tracked inactiveReasons = [];
  @tracked messages = emptyInfinityModel();

  constructor(owner, options) {
    super(owner, options);
    this.conversation = options.conversation;
  }

  onVisit = task({ drop: true }, async () => {
    this.loadMessages.perform();

    try {
      this.#setConversation(await this.#fetchShow());
    } catch (error) {
      await this.returnToConversationList.perform(error);
    }
  });

  onLeave = task({ drop: true }, async () => {
    this.loadMessages.cancelAll();
    this.reloadFromShow.cancelAll();
  });

  reloadFromShow = task({ restartable: true }, async () => {
    try {
      this.#setConversation(await this.#fetchShow());
    } catch (error) {
      this.#reportConversationError(error);
      if (error instanceof ForbiddenError) {
        await this.messagingNavigation.toConversationList({ recordHistory: false });
      }
    }
  });

  loadMessages = task({ restartable: true }, async () => {
    try {
      this.messages = await this.#fetchFirstPage();
    } catch (error) {
      this.messages = emptyInfinityModel();
      await this.returnToConversationList.perform(error);
    }
  });

  // Promote create responses sideload system rows into the store. Pull any
  // persisted system messages for this conversation that are not yet on the
  // open infinity list (socket may have delivered them already).
  mergePromotedSystemMessages() {
    const conversationId = this.conversation?.id;
    if (conversationId === null || conversationId === undefined) return;

    const systemMessages = this.store.peekAll(MODEL_NAMES.MESSAGE).filter(message => {
      if (!message?.isSystem || message.id === null || message.id === undefined) return false;
      return String(message.belongsTo('conversation').id()) === String(conversationId);
    });

    this.insertMissingMessages(systemMessages);
  }

  insertMissingMessages(records) {
    const presentIds = new Set(
      (this.messages?.content ?? [])
        .map(message => message.id)
        .filter(id => id !== null && id !== undefined)
        .map(id => String(id))
    );

    records?.forEach(record => {
      if (record?.id === null || record?.id === undefined || presentIds.has(String(record.id))) {
        return;
      }

      this.messages?.unshiftObject(record);
      presentIds.add(String(record.id));
    });
  }

  loadMoreMessages = task({ drop: true }, async model => {
    try {
      await this.infinity.loadNextPage(model);
    } catch (err) {
      this.messagingLogging.error(err, {
        context: 'conversation loadMoreMessages',
        conversationId: this.conversation?.id,
      });
      this.#handleConversationError();
    }
  });

  returnToConversationList = task({ drop: true }, async error => {
    this.#reportConversationError(error);
    await this.messagingNavigation.toConversationList({ recordHistory: false });
  });

  #fetchShow() {
    return this.store.findRecord(MODEL_NAMES.CONVERSATION, this.conversation.id, {
      include: 'users,last_message',
      reload: true,
    });
  }

  #fetchFirstPage() {
    return this.infinity.model(
      MODEL_NAMES.MESSAGE,
      {
        conversationId: this.conversation.id,
        include: 'conversation,user',
        filter: {
          'include_system': this.messaging.currentProfile.isClinician,
        },
        ...pageNumberInfinityOptions(MESSAGES_PER_PAGE),
      },
      pageNumberInfinityModel(MESSAGES_PER_PAGE)
    );
  }

  #setConversation(conversation) {
    this.conversation = conversation;
    this.inactiveReasons = inactiveReasonsFromConflicts(conversation.conflicts);
  }

  #reportConversationError(error) {
    this.messagingLogging.logGeneralError(error, { conversationId: this.conversation?.id });
    this.#handleConversationError();
  }

  #handleConversationError() {
    this.toast.error(this.intl.t('emc.general_error'));
  }
}
